import React, { useState, useEffect, useRef, useCallback } from "react";
import axiosInstance from "../helpers/axiosInstance";
import { socket } from "../helpers/socketInstance";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import Swal from 'sweetalert2';
import { useNavigate } from "react-router-dom";
// import { useLoading } from '../context/LoadingContext';
import './bayInformation.css';

const BayInfo = () => {
    const [bayStatus, setBayStatus] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [shop, setShop] = useState(null);
    const [shops, setShops] = useState([]);
    const [searchShop, setSearchShop] = useState("");
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [hoveredBay, setHoveredBay] = useState(null);
    const dropdownRef = useRef(null);
    const [statusCounts, setStatusCounts] = useState({
        occupied: 0,
        registered: 0,
        empty: 0,
        plannedempty: 0,
        unplanned: 0,
        unplannedoccupied: 0
    });
    // const { updateLoading } = useLoading();
    const navigate = useNavigate();


    // Fetch Bay Data
    const fetchBayConfiguration = useCallback(async (shopId) => {
        if (!shopId) return;

        // updateLoading(true); // loader starts immediately

        try {
            const response = await axiosInstance.get(`/api/bays/${shopId}/bays`);

            if (!response.data?.bays || !Array.isArray(response.data.bays)) {
                console.error("Invalid API response structure:", response.data);
                return;
            }

            const transformedData = response.data.bays.map((bay) => {
                let rawStatus = bay.status || "unplanned";
                let displayStatus = rawStatus;

                if (rawStatus === "unplanned_occupied") {
                    displayStatus = "occupied";
                }

                const updatedAt = bay.BayStatusLogs.length > 0
                    ? bay.BayStatusLogs[0].updatedAt
                    : null;

                const battery = bay.BayStatusLogs.length > 0
                    ? bay.BayStatusLogs[0].battery
                    : null;

                const hoursSinceUpdate = updatedAt
                    ? (new Date() - new Date(updatedAt)) / (1000 * 60 * 60)
                    : Infinity;

                if (displayStatus === "occupied" && hoursSinceUpdate > 18) {
                    displayStatus = "empty";
                }

                return {
                    id: bay.id,
                    number: bay.number,
                    status: displayStatus,
                    rawStatus,
                    updatedAt,
                    battery,
                    shop_id: response.data.shop_id,
                    vehicle_no: bay.vehicle_no || null,
                };
            });

            const counts = transformedData.reduce((acc, bay) => {
                const normalized = bay.status.replace(/[-_\s]/g, "").toLowerCase();
                acc[normalized] = (acc[normalized] || 0) + 1;
                return acc;
            }, {
                occupied: 0,
                registered: 0,
                empty: 0,
                plannedempty: 0,
                unplanned: 0,
                unplannedoccupied: 0
            });

            setBayStatus(transformedData);
            setStatusCounts(counts);
        } catch (error) {
            console.error("Error fetching bay data:", error);
        } finally {
            // updateLoading(false); // loader ends after data is ready
        }
    }, []);


    // Fetch Shops
    const fetchShops = useCallback(() => {
        // updateLoading(true);
        axiosInstance.get("/api/shop/shops")
            .then(response => {
                const shops = response.data.shops || [];
                setShops(shops);

                if (shops.length > 0) {
                    const defaultShop = shops[0];
                    setShop(defaultShop.id);
                    setSearchShop(defaultShop.shop_name);
                    fetchBayConfiguration(defaultShop.id);
                }
            })
            .catch(console.error)
        // .finally(() => updateLoading(false));
    }, [fetchBayConfiguration]);

    useEffect(() => {
        fetchShops();
    }, [fetchShops]);

    // Handle WebSocket Updates
    useEffect(() => {
        if (!shop) return;

        // Fetch immediately once
        fetchBayConfiguration(shop);

        // Set up interval
        const intervalId = setInterval(() => {
            fetchBayConfiguration(shop);
        }, 15000);

        // Clear interval on cleanup
        return () => clearInterval(intervalId);
    }, [shop, fetchBayConfiguration]);

    useEffect(() => {
        const handleBayUpdate = (updatedBay) => {
            console.log("Received bayStatusUpdated event:", updatedBay);

            let status = updatedBay.status;
            if (status === "unplanned_occupied") {
                status = "occupied";
            }

            setBayStatus((prevBays) => {
                const updatedBayId = Number(updatedBay.bay_id);
                const newBays = prevBays.map(bay =>
                    bay.id === updatedBayId
                        ? {
                            ...bay,
                            status: updatedBay.status === "not_planned_occupied" ? "occupied" : updatedBay.status,
                            updatedAt: updatedBay.updatedAt || bay.updatedAt,
                            vehicle_no: updatedBay.vehicle_no || bay.vehicle_no
                        }
                        : bay
                );

                // Recompute counts
                const counts = newBays.reduce((acc, bay) => {
                    const normalized = bay.status.replace(/[-_\s]/g, "").toLowerCase();
                    acc[normalized] = (acc[normalized] || 0) + 1;
                    return acc;
                }, {
                    occupied: 0,
                    registered: 0,
                    empty: 0,
                    plannedempty: 0,
                    notplanned: 0,
                    notplannedoccupied: 0
                });

                setStatusCounts(counts);

                return newBays;
            });
        };

        socket.on("bayStatusUpdated", handleBayUpdate);

        return () => {
            socket.off("bayStatusUpdated", handleBayUpdate);
        };
    }, []);

    // Count Status (including Empty)
    const totalBays = bayStatus.length;
    // const statusCounts = bayStatus.reduce(
    //     (counts, bay) => {
    //         const normalizedStatus = bay.status.replace(/[-_\s]/g, "").toLowerCase();
    //         counts[normalizedStatus] = (counts[normalizedStatus] || 0) + 1;
    //         return counts;
    //     },
    //     {
    //         occupied: 0,
    //         registered: 0,
    //         empty: 0, // ✅ Dedicated empty count
    //         plannedempty: 0,
    //         notplanned: 0,
    //         notplannedoccupied: 0,
    //     }
    // );

    // Filter Bays by Search
    const filteredBays = bayStatus.filter((bay) => {
        if (shop && Number(bay.shop_id) !== Number(shop)) return false;

        const normalizedSearchQuery = searchQuery?.toLowerCase().trim().replace(/[-_\s]/g, "");
        const normalizedStatus = bay.status?.toLowerCase().replace(/[-_\s]/g, "");
        const bayNumber = bay.number?.toString().toLowerCase() || "";

        if (!normalizedSearchQuery) return true;

        return normalizedStatus.includes(normalizedSearchQuery) || bayNumber.includes(normalizedSearchQuery);
    });

    // Shop Dropdown Handlers
    const handleInputClick = () => {
        setIsDropdownOpen(true);
        setSearchShop("");
    };

    const handleShopSelect = (shopId, shopName) => {
        setShop(shopId);
        setSearchShop(shopName);
        setIsDropdownOpen(false);
        fetchBayConfiguration(shopId);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const filteredShops = searchShop
        ? shops.filter((s) => s.shop_name.toLowerCase().includes(searchShop.toLowerCase()))
        : shops;


    const handleExport = async () => {
        try {
            const response = await axiosInstance.get("/api/bays/report/bay-status");
            const baysData = response.data.data;

            if (!Array.isArray(baysData) || baysData.length === 0) {
                throw new Error("No data available to export");
            }

            const excelData = baysData.map((bay, index) => ({
                "Sl. No.": index + 1,
                "Bay Number": bay.bay_number,
                "Current Status": bay.current_status || "N/A",
                "Last Sync": bay.last_sync
                    ? new Date(bay.last_sync).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })
                    : "N/A",
                "Sensor Status": bay.statusNotChange ? "Inactive" : "Active",
            }));

            const worksheet = XLSX.utils.json_to_sheet(excelData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Bay Status");

            Swal.fire({
                icon: "success",
                title: "Export Started",
                text: "File Downloading has Started!",
                confirmButtonText: "OK",
            });

            const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
            const dataBlob = new Blob([excelBuffer], { type: "application/octet-stream" });

            saveAs(dataBlob, "BayStatusReport.xlsx");
        } catch (error) {
            console.error("Export failed:", error);
            Swal.fire({
                title: "Error!",
                text: "Something went wrong while exporting. Redirecting to Bay Status Report page...",
                icon: "error",
                confirmButtonText: "OK",
            }).then(() => {
                navigate("/report/bay");
            });
        }
    };

    return (
        <div className="container bayinfo-contain mt-1">
            {/* Status Summary */}
            <div className="status-bar bayinfo-statusbar d-flex justify-content-between align-items-center flex-wrap mb-3">
                <div className="status-item text-center flex-fill m-2 p-1 total-bays">
                    <span className="d-block fw-bold">Total Bays</span>
                    <span className="fs-5">{totalBays}</span>
                </div>
                <div className="status-item text-center flex-fill m-2 p-1 occupied">
                    <span className="d-block fw-bold">Occupied</span>
                    <span className="fs-5">{statusCounts["occupied"]}</span>
                </div>
                <div className="status-item text-center flex-fill m-2 p-1 registered">
                    <span className="d-block fw-bold">Registered</span>
                    <span className="fs-5">{statusCounts["registered"]}</span>
                </div>
                <div className="status-item text-center flex-fill m-2 p-1 empty">
                    <span className="d-block fw-bold">Empty</span>
                    <span className="fs-5">{statusCounts["empty"]}</span>
                </div>
                <div className="status-item text-center flex-fill m-2 p-1 planned-empty">
                    <span className="d-block fw-bold">Planned Empty</span>
                    <span className="fs-5">{statusCounts["plannedempty"]}</span>
                </div>
                <div className="status-item text-center flex-fill m-2 p-1 not-planned">
                    <span className="d-block fw-bold">Unplanned</span>
                    <span className="fs-5">{statusCounts["unplanned"]}</span>
                </div>
                <div className="status-item text-center flex-fill m-2 p-1 not-planned-occupied">
                    <span className="d-block fw-bold">Unplanned Occupied</span>
                    <span className="fs-5">{statusCounts["unplannedoccupied"]}</span>
                </div>
            </div>

            {/* Shop Selector & Search */}
            <div className="d-flex justify-content-between align-items-center mb-5">
                <div className="d-flex align-items-center w-50">
                    <div
                        className="d-flex align-items-center border shadow-sm px-3 py-1 rounded w-50 position-relative"
                        ref={dropdownRef}
                        style={{ background: "#fff", height: "40px" }}
                    >
                        <label className="fw-bold me-2">Shop <span style={{ color: "gray", fontWeight: "normal" }}>|</span></label>
                        <input
                            type="text"
                            className="form-control border-0 px-2 py-0"
                            placeholder="Search or Select Shop"
                            value={searchShop}
                            onChange={(e) => {
                                setSearchShop(e.target.value);
                                setIsDropdownOpen(true);
                                setSearchQuery(e.target.value);
                            }}
                            onClick={handleInputClick}
                            style={{ flex: 1, outline: "none", boxShadow: "none", height: "35px" }}
                        />
                        {isDropdownOpen && (
                            <ul className="dropdown-menu show w-100 shadow-sm position-absolute" style={{ top: "100%", left: 0 }}>
                                {filteredShops.length > 0 ? (
                                    filteredShops.map((s) => (
                                        <li key={s.id} className="dropdown-item" onClick={() => handleShopSelect(s.id, s.shop_name)}>
                                            {s.shop_name}
                                        </li>
                                    ))
                                ) : (
                                    <li className="dropdown-item text-muted">No shops found</li>
                                )}
                            </ul>
                        )}

                    </div>
                </div>
                <div className="w-25 d-flex align-items-center position-relative">
                    <input
                        type="text"
                        className="form-control shadow-sm"
                        placeholder="Search by Number or Status"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />

                    <button
                        className="btn ms-2 text-white d-flex align-items-center"
                        onClick={handleExport}
                        title="Download Sensor Data"
                        style={{ whiteSpace: "nowrap", height: "38px", backgroundColor: "#0A4595" }}
                    >
                        <i className="bi bi-upload me-1"></i> Export
                    </button>

                </div>

            </div>

            {/* Bay Cards */}
            <div className="scrollable-bay-list bayinfo-scrollable">
                <div className="row">
                    {filteredBays.length === 0 ? (
                        <div className="text-center text-muted py-3">
                            No bays available for the selected shop.
                        </div>
                    ) : (
                        filteredBays.map((bay) => {
                            const statusClass = bay.status
                                ?.toLowerCase()
                                .replace(/[\s_]+/g, "-");

                            return (
                                <div
                                    className="col-12 col-sm-6 col-md-4 col-lg-2 mb-3 d-flex align-items-stretch"
                                    key={bay.id}
                                    onMouseEnter={() => setHoveredBay(bay.id)}
                                    onMouseLeave={() => setHoveredBay(null)}
                                    style={{ maxWidth: "120px", minHeight: "50px" }}
                                >
                                    <div className={`card bayinfo-card shadow-sm ${statusClass} w-100`}>
                                        <div className="card-body text-center p-2">
                                            {/* Bay Number */}
                                            <p className="card-title fw-bold text-start mt-0 mb-2 fs-6">
                                                {bay.number}
                                            </p>

                                            {/* ---- Status Display (Existing Logic) ---- */}
                                            {hoveredBay === bay.id ? (
                                                bay.status.toLowerCase() === "registered" ? (
                                                    <p className="text-muted small mb-0">
                                                        Data not received from device
                                                    </p>
                                                ) : (() => {
                                                    const updatedTime = bay.updatedAt ? new Date(bay.updatedAt) : null;
                                                    const now = new Date();
                                                    const hoursDiff = updatedTime
                                                        ? (now - updatedTime) / (1000 * 60 * 60)
                                                        : null;

                                                    if (!updatedTime || hoursDiff > 18) {
                                                        return (
                                                            <>
                                                                <p
                                                                    className="text-danger small mb-0"
                                                                    style={{ fontSize: "12px" }}
                                                                >
                                                                    ❌ Data not received in last 18 hours
                                                                </p>
                                                            </>
                                                        );
                                                    } else {
                                                        return (
                                                            <>
                                                                {bay.vehicle_no && (
                                                                    <p className="text-muted small mb-1">
                                                                        {bay.vehicle_no}
                                                                    </p>
                                                                )}
                                                                <p className="small text-secondary mb-0">
                                                                    {updatedTime.toLocaleString()}
                                                                </p>
                                                            </>
                                                        );
                                                    }
                                                })()
                                            ) : (
                                                <p
                                                    className={`fw-semibold mb-1 ${(!bay.updatedAt ||
                                                        (new Date() - new Date(bay.updatedAt)) / (1000 * 60 * 60) > 18) &&
                                                        bay.status.toLowerCase() !== "registered"
                                                        ? "text-danger"
                                                        : "text-dark"
                                                        }`}
                                                >
                                                    {bay.status}
                                                </p>
                                            )}

                                            {/* ---- ✅ Battery Status (Always Visible) ---- */}
                                            {(() => {
                                                const getBatteryInfo = (battery) => {
                                                    if (battery == null) return { color: "gray", percent: 0, label: "N/A" };
                                                    if (battery >= 80) return { color: "green", percent: battery, label: `${battery}%` };
                                                    if (battery >= 21) return { color: "orange", percent: battery, label: `${battery}%` };
                                                    return { color: "red", percent: battery, label: `${battery}%` };
                                                };
                                                const batteryInfo = getBatteryInfo(bay.battery);

                                                return (
                                                    <span
                                                        className="small"
                                                        style={{
                                                            fontSize: "13px",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center", // centers horizontally
                                                            gap: "6px",
                                                            lineHeight: 1, // ensures text is centered vertically
                                                            fontWeight: "600",
                                                        }}
                                                    >
                                                        {/* ✅ Battery Icon */}
                                                        {/* <span
                                                            style={{
                                                                position: "relative",
                                                                display: "inline-block",
                                                                width: "24px",
                                                                height: "12px",
                                                                border: "2px solid #333",
                                                                borderRadius: "3px",
                                                                boxSizing: "border-box",
                                                            }}
                                                        >
                                                           
                                                            <span
                                                                style={{
                                                                    display: "block",
                                                                    height: "100%",
                                                                    width: `${batteryInfo.percent}%`,
                                                                    backgroundColor: batteryInfo.color,
                                                                    borderRadius: "1px",
                                                                    transition: "width 0.3s ease",
                                                                }}
                                                            />
                                                        </span> */}

                                                        {/* Battery Label */}
                                                        <span style={{ display: "flex", alignItems: "center" }}>
                                                            {batteryInfo.label}
                                                        </span>
                                                    </span>


                                                );
                                            })()}

                                        </div>
                                    </div>

                                </div>
                            );
                        })
                    )}

                </div>
            </div>
        </div>
    );
};

export default BayInfo;
